/**
 * Les lots courants d'un chantier de construction, et leurs phases types.
 *
 * Décidé avec le patron le 30 octobre 2026 : un chantier tous corps d'état se
 * découpe en lots, un par corps de métier, choisis dans une liste courante ou
 * saisis à la main. Même règle que les phases types (`phaseTemplates.ts`) :
 * **des propositions, jamais une énumération**. Le lot reste du texte libre
 * en base ; un métier qui manque s'ajoute ici sans migration.
 *
 * L'ordre de la liste est celui d'une construction, du terrassement aux
 * extérieurs : c'est lui qui range les lots ajoutés, et il évite d'avoir à
 * les réordonner après coup. Les phases de chaque lot reprennent celles du
 * métier quand il en a déjà — le plombier d'un chantier tous corps d'état
 * fait les mêmes étapes que celui d'une entreprise de plomberie — sans les
 * étapes communes au chantier entier (installation, nettoyage), qui
 * appartiennent à tous et à aucun lot.
 */
import { phasesForTrade } from "./phaseTemplates.js";

export interface LotTemplate {
  name: string;
  phases: readonly string[];
}

/**
 * Le déroulé d'un métier, sans ce qui appartient au chantier entier :
 * l'installation de chantier sort, et « Finitions et nettoyage » devient
 * « Finitions » — le nettoyage final est celui de tous les lots.
 */
const DU_CHANTIER: Readonly<Record<string, string | null>> = {
  "Installation de chantier": null,
  "Installation et protections": "Protections",
  "Finitions et nettoyage": "Finitions",
};

function duMetier(trade: string): readonly string[] {
  return phasesForTrade(trade).flatMap((nom) => {
    if (!(nom in DU_CHANTIER)) return [nom];
    const autre = DU_CHANTIER[nom];
    return autre ? [autre] : [];
  });
}

export const LOT_TEMPLATES: readonly LotTemplate[] = [
  {
    name: "Terrassement et VRD",
    phases: ["Implantation", "Décapage et terrassement", "Réseaux enterrés", "Remblais et compactage"],
  },
  {
    name: "Gros œuvre",
    phases: [
      "Fondations",
      "Soubassement et vide sanitaire",
      "Dallage",
      "Élévation des murs",
      "Planchers",
      "Ouvertures et linteaux",
    ],
  },
  {
    name: "Charpente",
    phases: ["Approvisionnement ou fabrication", "Levage et pose", "Contreventement"],
  },
  { name: "Couverture et zinguerie", phases: duMetier("couverture") },
  { name: "Menuiseries extérieures", phases: duMetier("menuiserie") },
  {
    name: "Plâtrerie et isolation",
    phases: ["Ossatures et rails", "Isolation", "Pose des plaques", "Bandes et joints"],
  },
  { name: "Plomberie", phases: duMetier("plomberie") },
  { name: "Électricité", phases: duMetier("electricite") },
  { name: "Chauffage, ventilation, climatisation", phases: duMetier("climatisation") },
  {
    name: "Chape et carrelage",
    phases: ["Chape", "Séchage de la chape", "Carrelage des sols", "Faïence", "Joints et plinthes"],
  },
  {
    name: "Menuiseries intérieures",
    phases: ["Bâtis et portes", "Placards et rangements", "Escalier", "Plinthes et habillages"],
  },
  {
    name: "Sols souples et parquet",
    phases: ["Préparation du support", "Pose", "Finitions"],
  },
  { name: "Peinture", phases: duMetier("peinture") },
  {
    name: "Façade et ravalement",
    phases: ["Échafaudage", "Préparation du support", "Enduit ou parement", "Finitions de façade"],
  },
  { name: "Aménagements extérieurs", phases: duMetier("paysagisme") },
];

const norme = (nom: string) => nom.trim().toLowerCase();

/** Les lots qui restent à poser : ceux déjà sur le chantier sortent de la liste. */
export function remainingLots(existants: readonly { name: string }[]): readonly string[] {
  const deja = new Set(existants.map((l) => norme(l.name)));
  return LOT_TEMPLATES.map((l) => l.name).filter((nom) => !deja.has(norme(nom)));
}

/**
 * Les phases proposées dans un lot. Un lot de la liste reçoit ses phases ; un
 * lot saisi à la main, le déroulé du métier de l'entreprise — une liste vide
 * renverrait au clavier.
 */
export function phasesForLot(
  lotName: string | null | undefined,
  trade: string | null | undefined,
): readonly string[] {
  const lot = lotName ? LOT_TEMPLATES.find((l) => norme(l.name) === norme(lotName)) : undefined;
  return lot?.phases ?? phasesForTrade(trade);
}

/** Celles qui restent à poser dans le lot, les intitulés déjà présents retirés. */
export function remainingLotPhases(
  lotName: string | null | undefined,
  trade: string | null | undefined,
  existantes: readonly { name: string }[],
): readonly string[] {
  const deja = new Set(existantes.map((p) => norme(p.name)));
  return phasesForLot(lotName, trade).filter((nom) => !deja.has(norme(nom)));
}

/**
 * Le rang d'un lot nouveau. Un lot de la liste prend sa place dans l'ordre
 * d'une construction — la plomberie avant la peinture, même ajoutée après —
 * un lot saisi à la main se range à la fin.
 */
export function lotSortOrder(name: string): number {
  const i = LOT_TEMPLATES.findIndex((l) => norme(l.name) === norme(name));
  return i >= 0 ? (i + 1) * 10 : 900;
}
